// ---------------------------------------------------------------------------
// Curated "great roads" registry.
//
// Fastest-path routing only discovers pleasantness ALONG the direct corridor
// (see the 500 m anchor filter in generateRoutes). A genuinely beautiful road
// that's a deliberate detour off that line — Round Hill Road through
// backcountry Greenwich, say — is never even generated as a candidate. This
// registry is the fix: a hand-curated list of roads worth leaving the direct
// line for. When a trip passes near one, generateRoutes injects a waypoint
// THROUGH it (bypassing the corridor filter) and runs at scenic tolerance so
// the engine commits to it instead of the faster arterial. Same mechanism the
// Pacific Coast Highway uses (pchWaypoints.ts).
//
// SOURCE OF TRUTH: road names + towns live in content/greatRoads.ts (kept in
// sync with the "Great Roads" spreadsheet). Their geometry is looked up
// automatically by scripts/resolveGreatRoads.mts into content/greatRoadsCoords
// — nobody hand-enters coordinates. A road becomes active here once it has a
// resolved point.
// ---------------------------------------------------------------------------

import type { LngLat } from "@/utils/polyline";
import { haversine, cumulativeDistances, nearestPointOnPolyline } from "@/utils/polyline";
import { GREAT_ROADS } from "@/content/greatRoads";
import { GREAT_ROAD_COORDS } from "@/content/greatRoadsCoords";
import { GREAT_ROAD_BLURBS } from "@/content/greatRoadBlurbs";
import { GREAT_ROAD_SCORES } from "@/content/greatRoadScores";
import { isSeasonallyClosedRoad } from "@/utils/seasonal";
import hudsonRoadsData from "@/content/hudsonRoads.json";
import hudsonRoadsCoords from "@/content/hudsonRoadsCoords.json";
import type { GreatDrive } from "@/content/greatDrivesTypes";

export interface ScenicRoad {
  id: string;
  name: string;
  /** Where it is, e.g. "Greenwich, CT". */
  region: string;
  /** Ordered [lng, lat] sample points lying ON the road. */
  samples: LngLat[];
  /** How close (km) each trip endpoint must be for the road to be suggested. */
  maxOffsetKm?: number;
  /** Travel modes this entry is curated for. */
  modes: RoadMode[];
  /** Optional one-line descriptor used in scoring credit + explanations. */
  blurb?: string;
  /** Curated quality score 1-3 (3 = national-caliber). Breaks ties between
   *  comparable candidates so the better road wins. 0 = unscored. */
  score?: number;
}

// How much of an "on-the-way" head start each score tier gets when ranking
// curated candidates, expressed in km of detour. A score-3 road is treated as
// ~4 km closer than it really is, so among roads that are otherwise a wash the
// more iconic one wins — but a far-flung gem still can't drag the trip across
// town (the maxOffsetKm gate already bounds candidates to the corridor).
const SCORE_BONUS_KM: Record<number, number> = { 3: 4, 2: 1.5, 1: 0 };
const scoreBonusKm = (score?: number): number => SCORE_BONUS_KM[score ?? 0] ?? 0;

// How far a trip may pass and still have the road injected, by quality. A
// national-caliber road (3) is worth a real detour; a lesser one isn't. Also
// caps corrupted maxOffsetKm values.
const offsetCapForScore = (score?: number): number => (score === 3 ? 12 : score === 2 ? 9 : 8);

type RoadMode = "driving" | "walking" | "biking";

const DEFAULT_MAX_OFFSET_KM = 15;

// When injecting curated candidates, also offer the most dramatic gem the trip
// would actually drive end-to-end (e.g. Round Hill Road), not only the
// least-detour clip near the destination (e.g. Lake Avenue). This GENERATES the
// gem route into the pool; CURATED_WINS_SCENIC_SLOT (below) then lets it claim
// the Scenic card. The two are a pair — the gem must be generated before it can
// win. Flip to false to revert to least-detour-only injection.
export const SCENIC_CURATED_DETOUR = true;

// SCOPED (revertible): let a route that genuinely DRIVES a curated 'great road'
// claim the SCENIC slot even when a plain mapbox route is a touch faster — a
// deliberate backcountry detour (Round Hill Road) is exactly what Scenic is for.
// This only affects the Scenic/Adventure slot (never Direct or the primary
// Pleasant pick), only fires when the route is step-confirmed on ≥0.75 mi of a
// curated road, and is time-bounded by curatedScenicCap in scoring.ts — so it
// can't hijack a normal commute. Flip to false to revert.
export const CURATED_WINS_SCENIC_SLOT = true;

// Per-mode cap on how far a trip may pass and still be offered the curated
// entry. Driving roads are long and rural, so we trust the resolved radius.
// Walking streets are a single block — only surface one you're nearly on top
// of. Biking sits in between.
const MODE_OFFSET_CAP_KM: Record<RoadMode, number> = {
  driving: Infinity,
  biking: 6,
  walking: 2,
};

// Build the active registry: every curated road that has resolved geometry.
// `samples` is the multi-point road geometry from the geometry resolver when
// available (so a long corridor matches anywhere along it); otherwise it falls
// back to the single validated point.
/**
 * GUARD against corrupted resolver geometry. A name-based resolver can match
 * several different roads that share a name (the Greenwich, Bedford AND Brooklyn
 * "Round Hill Road" all merged into one entry, samples sprawling ~40 km with a
 * point down at lat 40.79). Routing a waypoint "through" that smear is garbage.
 * Keep only the samples clustered around the VALIDATED centroid `point` (which
 * was checked against the road's town), within a generous single-road radius.
 */
export function clusterSamplesNearPoint(
  point: LngLat,
  samples: LngLat[],
  // 15km, not 8: a single real road can be 20–25km long, so 8km would chop the
  // ends off a legit scenic road. Merged-road smears (Round Hill: 35–40km
  // outliers) are still well outside 15km, so they're still dropped.
  radiusKm = 15,
): LngLat[] {
  const near = samples.filter((s) => distKm(point, s) <= radiusKm);
  return near.length >= 1 ? near : [point];
}

export const SCENIC_ROADS: ScenicRoad[] = GREAT_ROADS.flatMap((r) => {
  const c = GREAT_ROAD_COORDS[r.id] as
    | { point: LngLat; samples?: LngLat[]; maxOffsetKm?: number }
    | undefined;
  if (!c) return [];
  const rawSamples = c.samples && c.samples.length >= 1 ? c.samples : [c.point];
  const samples = clusterSamplesNearPoint(c.point, rawSamples);
  const score = GREAT_ROAD_SCORES[r.id] ?? 0;
  return [
    {
      id: r.id,
      name: r.name,
      region: `${r.city}, ${r.state}`,
      samples,
      // Reach scales with quality: a national-caliber (score 3) road is worth a
      // bigger detour than a minor one. Still clamps the corrupted maxOffsetKm
      // (one entry stored 18) so nothing reaches absurdly far.
      maxOffsetKm: Math.min(c.maxOffsetKm ?? DEFAULT_MAX_OFFSET_KM, offsetCapForScore(score)),
      modes: r.modes ?? ["driving"],
      blurb: GREAT_ROAD_BLURBS[r.id],
      score,
    },
  ];
});

// ---- Curated DRIVING roads (state-by-state great_drive adapter) ------------
// LAZY-LOADED, like the walks: the driving store is ~2MB+ and only drivers need
// it, so it's code-split out of the main bundle and pulled the first time a
// driving route is generated (via loadGreatDrives()). The good roads
// (great_road + pleasant_connector) join SCENIC_ROADS so the driving matcher
// routes TOWARD them (great = score 3 / 2km reach, connector = 2 / 0.7km). The
// "avoid" roads feed AVOID_DRIVE_SEGMENTS, which steers routes AWAY via the
// penalty below.

/** A stroad / ugly / high-stress road the router should steer AWAY from. */
export interface AvoidDriveSegment {
  id: string;
  name: string;
  samples: LngLat[];
  point: LngLat;
  note: string;
}
// Populated by loadGreatDrives(). Empty until the first driving route loads it
// (so avoidDrivePenalty simply returns 0 on walks/bikes — correct).
export const AVOID_DRIVE_SEGMENTS: AvoidDriveSegment[] = [];

// PER-STATE code-split: one JSON chunk per state, so a trip downloads only the
// states it touches (a NY drive pulls ~68KB, not the ~2.6MB national store).
// Vite turns this glob into a lazy chunk per file.
const driveStateChunks = import.meta.glob("../content/greatDrives/*.json") as Record<
  string,
  () => Promise<{ default: GreatDrive[] }>
>;
const loadedDriveStates = new Set<string>();
const driveStatePromises = new Map<string, Promise<void>>();

// A "NY/CT"-tagged road lives in BOTH state files; dedup by id so loading two
// adjacent states never injects the same road twice.
const seenDriveIds = new Set<string>();
function pushDriveRecords(all: GreatDrive[]): void {
  const drives = all.filter((d) => !seenDriveIds.has(d.id));
  for (const d of drives) seenDriveIds.add(d.id);
  SCENIC_ROADS.push(
    ...drives.filter((d) => d.category !== "avoid").map((d) => ({
      id: d.id,
      name: d.name,
      region: d.region,
      samples: d.samples,
      // Great roads are worth a real detour; connectors only a small nudge.
      maxOffsetKm: d.category === "great_road" ? 2.0 : 0.7,
      modes: ["driving"] as RoadMode[],
      blurb: d.blurb,
      score: d.category === "great_road" ? 3 : 2,
    })),
  );
  AVOID_DRIVE_SEGMENTS.push(
    ...drives.filter((d) => d.category === "avoid").map((d) => ({
      id: d.id, name: d.name, samples: d.samples, point: d.point, note: d.avoidNotes,
    })),
  );
}

/**
 * Load curated driving roads for the given 2-letter state abbreviations (e.g.
 * the states a trip passes through). Idempotent per state; states with no
 * curated file are skipped silently.
 */
// Full multi-sample geometry for the curated GREAT_ROADS store. Resolved by
// scripts/resolveGreatRoadsGeometry.mjs into greatRoadsCoords.json — but never
// previously wired into the app: SCENIC_ROADS was built from the points-only
// greatRoadsCoords.ts, so the corridor matcher saw every great road as a
// single centroid dot (found via the Briarcliff→Sleepy Hollow repro: the
// matcher can neither trim nor thread a road it sees as one dot). Lazy-loaded
// alongside the drive store so the ~360KB JSON stays out of the main bundle.
let roadGeometryPromise: Promise<void> | null = null;
function ensureGreatRoadGeometry(): Promise<void> {
  if (!roadGeometryPromise) {
    roadGeometryPromise = import("@/content/greatRoadsCoords.json").then((mod) => {
      const table = ((mod as { default?: unknown }).default ?? mod) as Record<
        string,
        { point?: LngLat; samples?: LngLat[] }
      >;
      for (const r of SCENIC_ROADS) {
        const g = table[r.id];
        if (g?.samples && g.samples.length > r.samples.length) {
          const anchor = (g.point ?? r.samples[0]) as LngLat;
          r.samples = clusterSamplesNearPoint(anchor, g.samples as LngLat[]);
        }
      }
    }).catch(() => { /* geometry is an enhancement — centroids still work */ });
  }
  return roadGeometryPromise;
}

export function loadGreatDrivesForStates(abbrs: string[]): Promise<void> {
  const tasks: Promise<void>[] = [ensureGreatRoadGeometry()];
  for (const raw of abbrs) {
    const ab = raw.toUpperCase();
    if (loadedDriveStates.has(ab)) continue;
    if (!driveStatePromises.has(ab)) {
      const key = Object.keys(driveStateChunks).find((k) => k.endsWith(`/${ab}.json`));
      if (!key) { loadedDriveStates.add(ab); continue; } // no curated data for this state
      const p = driveStateChunks[key]().then((mod) => {
        pushDriveRecords(mod.default);
        loadedDriveStates.add(ab);
      });
      driveStatePromises.set(ab, p);
    }
    const promise = driveStatePromises.get(ab);
    if (promise) tasks.push(promise);
  }
  return Promise.all(tasks).then(() => undefined);
}

/** Load ALL states' driving roads. Used by tests / audits; production loads
 *  only the trip's states via loadGreatDrivesForStates. */
export function loadGreatDrives(): Promise<void> {
  const allAbbrs = Object.keys(driveStateChunks).map(
    (k) => k.split("/").pop()!.replace(".json", ""),
  );
  return loadGreatDrivesForStates(allAbbrs);
}

/**
 * Penalty for a driving route that still travels a hand-marked "avoid" road
 * (stroad, ugly commercial strip, high-stress arterial). A route "uses" an
 * avoid road when it passes within `nearM` of at least two of the road's
 * sample points. Each such road costs points, capped — enough to break ties
 * toward a calmer alternative without nuking a route that has no other option.
 */
export function avoidDrivePenalty(coords: LngLat[], nearM = 40): number {
  if (!coords || coords.length < 2 || AVOID_DRIVE_SEGMENTS.length === 0) return 0;
  let hits = 0;
  // Built once (PLE-6) instead of once per sample. `distance` never reads it.
  const cum = cumulativeDistances(coords);
  for (const seg of AVOID_DRIVE_SEGMENTS) {
    let near = 0;
    for (const s of seg.samples) {
      if (nearestPointOnPolyline(s, coords, cum).distance <= nearM) near++;
      if (near >= 2) break;
    }
    if (near >= 2) hits++;
  }
  return Math.min(14, hits * 7);
}

// ---- Curated walking segments → LAZY-LOADED injectable walk roads ----------
// The curated walking segments (content/greatWalks — thousands across many
// cities, several MB) are the raw material for the "nice city streets" variety
// walk. They are walking-mode only, so a DRIVE never needs them — and loading
// several MB on every app start just to drive is pure waste (Morgan: "I don't
// want this to slow everything down… only when there is a walk"). So we DON'T
// bundle them into the app: SCENIC_ROADS holds only the (small) driving great
// roads up front, and the walk segments are dynamically imported + folded in the
// first time a WALK/BIKE route is generated, via loadGreatWalks(). The dynamic
// import() makes Vite split greatWalks into its own chunk, out of the main
// bundle. Idempotent; resolves instantly after the first load.
let greatWalksReady: Promise<void> | null = null;
export function loadGreatWalks(): Promise<void> {
  if (!greatWalksReady) {
    greatWalksReady = import("@/content/greatWalks").then(({ GREAT_WALKS }) => {
      SCENIC_ROADS.push(
        ...GREAT_WALKS.map((w) => ({
          id: w.id,
          name: w.name,
          region: w.region,
          samples: w.samples,
          maxOffsetKm: 0.8,
          modes: w.modes,
          blurb: w.blurb,
          score: w.score,
        })),
      );
    });
  }
  return greatWalksReady;
}

// ---- Hudson Valley Pleasant Road Index → injectable roads -----------------
// Morgan's hand-scored 3s/2s become roads the engine can BUILD A ROUTE THROUGH
// (not just score by name). Activates only for roads with resolved geometry
// (run scripts/resolveRoadGeometry.mjs --hudson). Kept SEPARATE from SCENIC_ROADS
// so the scenic-credit path (curatedRoadsTravelled) doesn't double-count with the
// Hudson scoring lever in scoring.ts — these are for INJECTION only.
interface HudsonRow {
  town?: string; county?: string; road?: string; name?: string;
  score?: number | null; why?: string | null;
}
type HudsonCoord = { point: LngLat; samples?: LngLat[]; maxOffsetKm?: number };
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const HUDSON_SCENIC_ROADS: ScenicRoad[] = (hudsonRoadsData as HudsonRow[]).flatMap((r) => {
  const name = r.name ?? r.road;
  const town = r.town ?? "";
  if (!name || (r.score ?? 0) < 2) return []; // only "prefer" / "actively prefer"
  const id = `${slug(name)}-${slug(town)}`;
  const c = (hudsonRoadsCoords as Record<string, HudsonCoord>)[id];
  if (!c || !(c.samples && c.samples.length >= 2)) return []; // needs resolved geometry
  return [{
    id,
    name,
    region: [town, r.county].filter(Boolean).join(", "),
    samples: clusterSamplesNearPoint(c.point, c.samples),
    maxOffsetKm: Math.min(c.maxOffsetKm ?? 8, offsetCapForScore(r.score ?? 2)),
    modes: ["driving"] as RoadMode[],
    blurb: r.why ?? undefined,
    score: r.score ?? 2,
  }];
});

// User-nominated great roads, fed in from api/roadNominations.ts before
// routing. Kept separate from the static SCENIC_ROADS so the curated catalog
// stays pure; the matcher considers both. Empty by default → no behavior change.
let NOMINATED_ROADS: ScenicRoad[] = [];

/** Replace the active set of user-nominated roads (region-scoped, prefetched).
 *  Pass [] to clear. */
export function setNominatedRoads(roads: ScenicRoad[]): void {
  NOMINATED_ROADS = roads;
}

/** Static curated roads plus any user-nominated ones currently in scope. */
function activeScenicRoads(): ScenicRoad[] {
  return NOMINATED_ROADS.length ? SCENIC_ROADS.concat(NOMINATED_ROADS) : SCENIC_ROADS;
}

export interface CuratedRoadOnRoute {
  name: string;
  region: string;
  blurb?: string;
}

/**
 * Which curated roads/streets does this route actually run along? Returns the
 * curated entries (matching `mode`) whose representative point is within
 * `maxMeters` of the route polyline. Used to (a) credit scenic points in
 * scoring and (b) let the explainer name the road specifically.
 */
export function curatedRoadsAlongRoute(
  coords: LngLat[] | undefined,
  mode: RoadMode = "driving",
  maxMeters = 220,
): CuratedRoadOnRoute[] {
  if (!coords || coords.length === 0) return [];
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const margin = 0.01; // ~1 km bbox pad for a cheap prefilter
  const maxKm = maxMeters / 1000;
  const out: CuratedRoadOnRoute[] = [];
  for (const road of activeScenicRoads()) {
    if (!road.modes.includes(mode)) continue;
    const p = road.samples[0];
    if (!p) continue;
    if (p[0] < minLng - margin || p[0] > maxLng + margin || p[1] < minLat - margin || p[1] > maxLat + margin) {
      continue;
    }
    let near = Infinity;
    for (const c of coords) {
      const d = distKm(p, c);
      if (d < near) {
        near = d;
        if (near <= maxKm) break;
      }
    }
    if (near <= maxKm) out.push({ name: road.name, region: road.region, blurb: road.blurb });
    if (out.length >= 4) break;
  }
  return out;
}

export interface CuratedRoadTravelled extends CuratedRoadOnRoute {
  /** Meters of this route's turn-by-turn steps actually ON the road. */
  meters: number;
}

interface RouteForTravel {
  coordinates?: LngLat[];
  steps?: { roadName?: string; roadRef?: string; distance?: number }[];
  distance?: number;
}

/**
 * Which curated roads does this route GENUINELY travel (not merely pass near)?
 * Starts from the roads near the polyline, then confirms each against the
 * route's own turn-by-turn steps and keeps only those driven at least
 * `minMeters` (and `minFraction` of the trip). Sorted by distance travelled,
 * longest first. This is the single signal used for BOTH the scenic-score
 * credit and the card headline, so they always agree.
 */
/** Canonicalize a road name for matching: lowercase, strip punctuation, and
 *  expand the common US street-type abbreviations so "Round Hill Rd" (what the
 *  routing engine's step says) matches "Round Hill Road" (what our list says).
 *  Used only for comparison — we always DISPLAY the original curated name. */
const ROAD_ABBR: Record<string, string> = {
  rd: "road", st: "street", ave: "avenue", av: "avenue", blvd: "boulevard",
  dr: "drive", ln: "lane", hwy: "highway", pkwy: "parkway", ct: "court",
  pl: "place", ter: "terrace", terr: "terrace", tpke: "turnpike", rte: "route",
  cir: "circle", sq: "square", trl: "trail", wy: "way", pt: "point",
  hwys: "highway", expy: "expressway", fwy: "freeway",
};
/** Registry entries are named "Road Name (part): town / town" — the suffix
 *  locates the segment for curators, not for drivers. Anything user-facing
 *  ("… featuring X", the loved-road voice line) shows just the road: split at
 *  the first ":" or "—" and drop parentheticals. (Morgan, Aug 18: "featuring
 *  Natchez Trace Parkway (south): Natchez / Port Gibson / Jackson" is not a
 *  sentence anyone says out loud.) Matching still uses the FULL name. */
export function displayRoadName(n: string): string {
  return n.split(/[:—]/)[0].replace(/\s*\([^)]*\)/g, "").trim();
}

function canonRoad(s: string): string {
  return s
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\b(rd|st|ave|av|blvd|dr|ln|hwy|pkwy|ct|pl|ter|terr|tpke|rte|cir|sq|trl|wy|pt|expy|fwy)\b/g,
      (m) => ROAD_ABBR[m] ?? m)
    .replace(/\s+/g, " ")
    .trim();
}

/** Meters of the route polyline that run ALONG a curated road's geometry —
 *  the route length whose midpoint snaps within `thresholdM` of the road's
 *  sample polyline. This is the reliable signal: an injected route that was
 *  deliberately routed THROUGH a curated road overlaps its geometry even when
 *  the engine's step labels don't name the road. Needs ≥2 samples (a real line);
 *  single-point roads fall back to step-name matching only. */
function metersOnRoadGeometry(routeCoords: LngLat[], samples: LngLat[], thresholdM = 35): number {
  if (samples.length < 2 || routeCoords.length < 2) return 0;
  const cum = cumulativeDistances(samples);
  let m = 0;
  for (let i = 1; i < routeCoords.length; i++) {
    const a = routeCoords[i - 1];
    const b = routeCoords[i];
    const mid: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const snap = nearestPointOnPolyline(mid, samples, cum);
    if (snap.distance <= thresholdM) m += haversine(a, b);
  }
  return m;
}

export function curatedRoadsTravelled(
  route: RouteForTravel,
  mode: RoadMode = "driving",
  opts: { minMeters?: number; minFraction?: number } = {},
): CuratedRoadTravelled[] {
  const coords = route.coordinates;
  if (!coords || coords.length < 2) return [];
  const steps = route.steps ?? [];
  const total = route.distance || steps.reduce((s, st) => s + (st.distance ?? 0), 0);
  const floor = Math.max(opts.minMeters ?? 800, total * (opts.minFraction ?? 0));

  // Route bounding box — cheap prefilter so we only geometry-check roads in the
  // region, not all ~1,200.
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const margin = 0.01;

  // Secondary signal: the route's own step labels name the road. Kept because
  // it's exact when present, but geometry overlap is what makes detection robust
  // for injected routes whose steps don't carry clean road names.
  const stepMeters = (name: string): number => {
    if (steps.length === 0) return 0;
    const needle = canonRoad(name);
    let m = 0;
    for (const st of steps) {
      const rn = canonRoad(st.roadName ?? "");
      const rr = canonRoad(st.roadRef ?? "");
      if ((rn && rn.includes(needle)) || (rr && rr.includes(needle))) m += st.distance ?? 0;
    }
    return m;
  };

  const out: CuratedRoadTravelled[] = [];
  for (const road of activeScenicRoads()) {
    if (!road.modes.includes(mode)) continue;
    // bbox prefilter: at least one of the road's samples must fall inside the
    // padded route bbox, else it can't be on this trip.
    let near = false;
    for (const p of road.samples) {
      if (p[0] >= minLng - margin && p[0] <= maxLng + margin && p[1] >= minLat - margin && p[1] <= maxLat + margin) {
        near = true;
        break;
      }
    }
    if (!near) continue;
    // Geometry overlap is the trustworthy signal. The step-NAME match is only
    // valid when the route ACTUALLY runs the road's geometry — otherwise a
    // common name credits a curated road far away (Morgan: a CT→Maine route
    // "featured" Cape Cod's Main Street because the route drives *a* Main Street
    // and the giant bbox happens to contain the Cape). Require real geometry
    // overlap before trusting the name so single-point / off-route roads with a
    // generic name can never be credited.
    const geomMeters = metersOnRoadGeometry(coords, road.samples);
    const meters = geomMeters > 0 ? Math.max(geomMeters, stepMeters(road.name)) : 0;
    if (meters >= floor) {
      out.push({ name: road.name, region: road.region, blurb: road.blurb, meters });
    }
  }
  return out.sort((a, b) => b.meters - a.meters);
}

/** Haversine distance in km between two [lng, lat] points. */
function distKm(a: LngLat, b: LngLat): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

function nearestSample(road: ScenicRoad, p: LngLat): { idx: number; km: number } {
  let bestIdx = -1;
  let bestKm = Infinity;
  for (let i = 0; i < road.samples.length; i++) {
    const k = distKm(p, road.samples[i]);
    if (k < bestKm) {
      bestKm = k;
      bestIdx = i;
    }
  }
  return { idx: bestIdx, km: bestKm };
}

export interface ScenicRoadMatch {
  road: ScenicRoad;
  /** [lng, lat] waypoint(s) to route through, in travel order. */
  waypoints: LngLat[];
}

/**
 * If a trip from `start` to `end` passes near a curated road (both endpoints
 * within its `maxOffsetKm`), return the road plus the waypoint(s) to inject so
 * the engine routes through it.
 *
 * When several curated roads qualify (common in dense areas like Bedford), pick
 * the one that adds the LEAST detour — i.e. the gem most "on the way" — so we
 * don't drag the trip toward an unrelated road that merely happens to be in
 * range.
 */
/**
 * The curated roads worth injecting for this trip, ranked by least detour
 * (most "on the way"). We inject several candidates rather than just the
 * single least-detour one: a trivial central road might add the least detour
 * yet barely be travelled, while a slightly bigger detour road gets driven for
 * miles. Generating a candidate through each lets the scorer — which credits
 * by ACTUAL travel — pick the route that genuinely runs a curated road.
 */
export function scenicRoadsForCorridor(
  start: LngLat,
  end: LngLat,
  opts: { maxWaypoints?: number; mode?: RoadMode; maxRoads?: number } = {},
): ScenicRoadMatch[] {
  const maxWaypoints = opts.maxWaypoints ?? 1;
  const mode = opts.mode ?? "driving";
  const maxRoads = opts.maxRoads ?? 1;
  const modeCap = MODE_OFFSET_CAP_KM[mode] ?? Infinity;
  const directKm = distKm(start, end);

  const cands: { match: ScenicRoadMatch; detourKm: number }[] = [];
  // Scan curated great roads AND Morgan's Hudson 3s/2s — both are roads we want
  // to actively build a route through.
  const today = new Date();
  for (const road of [...SCENIC_ROADS, ...HUDSON_SCENIC_ROADS]) {
    if (road.samples.length < 1) continue;
    if (!road.modes.includes(mode)) continue;
    // Never inject a scenic road that's seasonally snow-closed today.
    if (isSeasonallyClosedRoad(road.name, today)) continue;
    const maxOffsetKm = Math.min(road.maxOffsetKm ?? DEFAULT_MAX_OFFSET_KM, modeCap);
    const a = nearestSample(road, start);
    const b = nearestSample(road, end);
    // OLD GATE (removed): required the road to pass within maxOffsetKm of BOTH
    // trip endpoints. Fine for long corridors, fatal for errands — a curated
    // road can be squarely ON the way yet 3 km from one endpoint (Briarcliff→
    // Sleepy Hollow rejected BOTH Sleepy Hollow Road and Pocantico Street this
    // way, leaving Scenic with nothing). Proximity is now judged against the
    // CORRIDOR by the trimming window below; keep only a coarse sanity bound
    // so we don't window-scan roads in another county.
    // TRIP-SCALED (the T+L NYC→Windham screenshots — Morgan: "we need to
    // wire in that nyc to windham is especially good"): the fixed 6 km
    // endpoint bound was tuned on 20-minute errands. On a 140-mile trip it
    // rejected Kaaterskill Clove (11 km from Windham) before scoring ever
    // saw it. Long trips may now look proportionally farther afield —
    // 12% of the straight-line distance — while short trips keep the
    // tight original behavior (the scaling only exceeds 6 km beyond
    // ~50 km trips).
    if (Math.min(a.km, b.km) > Math.max(maxOffsetKm, 6, directKm * 0.12)) continue;

    // ON-CORRIDOR TRIMMING (Sleepy Hollow fix — replaces the old all-or-nothing
    // gate). The old gate injected the road's ENTIRE nearest-A..nearest-B
    // stretch and rejected the whole road if ANY of it sat too far off the
    // start→end axis. Real casualty (Briarcliff→Sleepy Hollow, 2.9 km direct):
    // curated Sleepy Hollow Road runs a parallel valley — its southern half is
    // genuinely on the way, but its northern reach pushed worstOff to ~4 km, so
    // the road was never even considered and Scenic fell back to a Valhalla
    // loop. Now: keep only the CONTIGUOUS run of samples that lies within the
    // per-trip off-axis cap and inject just that. Lasso safety is preserved —
    // everything injected is within the cap by construction, and generateRoutes
    // still verifies the produced route truly traverses the road.
    //
    // Off-axis cap: curated great roads (maxOffsetKm 2.0) earn a 2 km floor —
    // a parallel beauty one valley over IS the point of the app. Connectors
    // keep the tight 0.8 km floor so errands don't wander.
    const offFloor = Math.max(0.8, Math.min(road.maxOffsetKm ?? 0.8, 2.0));
    // Off-axis ceiling scales with the trip too (same T+L fix): the flat
    // 5 km ceiling made every iconic road mathematically unreachable on
    // road trips — the "straight line" NYC→Windham is a fiction that runs
    // 16 km east of Kaaterskill Clove. Now: 5 km up to ~40 km trips,
    // then 12% of straight-line distance, hard-capped at 30 km so a
    // cross-country drive can't lasso a county sideways. The existing
    // detour budget + 2× duration sanity cap still bound total time.
    const offCeil = Math.min(Math.max(5, directKm * 0.12), 30, modeCap);
    const maxOffKm = Math.min(offCeil, Math.max(offFloor, directKm * 0.25));

    const lo = Math.min(a.idx, b.idx);
    const hi = Math.max(a.idx, b.idx);
    const offs = road.samples.map((s) => corridorOffset(s, start, end));
    // Longest contiguous in-cap window inside [lo..hi].
    let winLo = -1;
    let winHi = -1;
    let curLo = -1;
    for (let i = lo; i <= hi; i++) {
      if (offs[i].offKm <= maxOffKm) {
        if (curLo < 0) curLo = i;
        // >= so a single-sample window (a road that touches the corridor at
        // one resolved point, e.g. Pocantico St) is still recorded.
        if (winLo < 0 || i - curLo >= winHi - winLo) { winLo = curLo; winHi = i; }
      } else {
        curLo = -1;
      }
    }
    if (winLo < 0) continue; // no part of this road is on the way

    // Detour measured against the TRIMMED window's midpoint (the part we
    // actually route through), not the whole road's midpoint.
    const via = road.samples[Math.floor((winLo + winHi) / 2)];
    const rawDetourKm = distKm(start, via) + distKm(via, end) - directKm;
    // Quality-weighted detour: a more iconic road (score 3 > 2 > 1) is treated
    // as a little more "on the way", so it wins close calls without letting a
    // far gem hijack the trip.
    const detourKm = rawDetourKm - scoreBonusKm(road.score);

    // SEGMENT-BASED injection over the TRIMMED window (entry → interior →
    // exit, in travel order) so the router runs the LENGTH of the on-corridor
    // stretch instead of clipping or lollipopping a single point.
    let waypoints: LngLat[];
    if (winHi - winLo < 1) {
      waypoints = [road.samples[winLo]];
    } else {
      const stretch = road.samples.slice(winLo, winHi + 1); // entry..exit, geometry order
      const want = Math.min(4, stretch.length);
      const picks: LngLat[] = [];
      for (let i = 0; i < want; i++) {
        const t = want === 1 ? 0 : i / (want - 1);
        picks.push(stretch[Math.round(t * (stretch.length - 1))]);
      }
      if (a.idx > b.idx) picks.reverse(); // travel order: entry nearest start
      waypoints = picks.filter((p, i) => i === 0 || p[0] !== picks[i - 1][0] || p[1] !== picks[i - 1][1]);
    }

    // ON-AXIS CHECK, softened: the old rule required a waypoint to project
    // onto the middle of the trip (t 0.08–0.92), which wrongly rejected
    // curated roads that live right AT the destination (Pocantico Street:
    // 0.76 km detour, rejected as a "spur"). A near-endpoint road with a
    // small ABSOLUTE detour is not a lasso — waive the axis test when the
    // detour is tiny for this trip.
    const tinyDetour = rawDetourKm <= Math.max(1.0, 0.35 * directKm);
    let anyOnAxis = false;
    for (const wp of waypoints) {
      const { t } = corridorOffset(wp, start, end);
      if (t >= 0.08 && t <= 0.92) anyOnAxis = true;
    }
    if (!anyOnAxis && !tinyDetour) continue;

    cands.push({ match: { road, waypoints }, detourKm });
  }

  cands.sort((x, y) => x.detourKm - y.detourKm);
  let picked = cands.slice(0, maxRoads);

  // LONG-TRIP SPREAD (the T+L Windham screenshots — Morgan: "all of the
  // detours are close to the starting point not the destination"): on a
  // road trip, dozens of near-zero-detour roads cluster in the first
  // county and fill every slot before the far end is even considered.
  // Past ~60 km, spread picks across the journey — destination third
  // claims first (the Catskills end is the REASON for a Catskills trip),
  // then middle, then early — and thread them in travel order.
  if (directKm > 60 && cands.length > maxRoads) {
    const tOf = (c: (typeof cands)[number]) => {
      const wps = c.match.waypoints;
      const mid = wps[Math.floor(wps.length / 2)];
      return corridorOffset(mid, start, end).t;
    };
    const spread: typeof cands = [];
    for (const [lo, hi] of [[0.55, 1.01], [0.28, 0.55], [0, 0.28]] as const) {
      if (spread.length >= maxRoads) break;
      const best = cands.find((c) => !spread.includes(c) && tOf(c) >= lo && tOf(c) < hi);
      if (best) spread.push(best);
    }
    for (const c of cands) {
      if (spread.length >= maxRoads) break;
      if (!spread.includes(c)) spread.push(c);
    }
    spread.sort((a, b) => tOf(a) - tOf(b)); // travel order for threading
    picked = spread.slice(0, maxRoads);
  }

  // Make sure a dramatic, traverse-the-whole-thing gem isn't crowded out by
  // trivial low-detour clips. Add the LARGEST-detour candidate that still sits
  // within a sane budget (roughly the trip's own length), so a backcountry road
  // you'd drive for miles competes alongside the near-the-destination clips.
  // The scorer then credits each by ACTUAL miles travelled, so the gem wins
  // when you really run it. Bounded to keep the injected count == maxRoads.
  // (Short trips only — the long-trip spread above already guarantees the
  // far end a slot, and this override would undo it.)
  if (SCENIC_CURATED_DETOUR && directKm <= 60 && cands.length > 0) {
    const cap = Math.max(8, directKm * 1.2);
    const gem = cands
      .filter((c) => c.detourKm <= cap)
      .sort((a, b) => b.detourKm - a.detourKm)[0];
    if (gem && !picked.includes(gem)) {
      picked = cands.slice(0, Math.max(1, maxRoads - 1)).concat(gem);
    }
  }

  return picked.map((c) => c.match);
}

/** Single best curated road for this corridor (least detour). */
export function scenicRoadForCorridor(
  start: LngLat,
  end: LngLat,
  opts: { maxWaypoints?: number; mode?: RoadMode } = {},
): ScenicRoadMatch | null {
  return scenicRoadsForCorridor(start, end, { ...opts, maxRoads: 1 })[0] ?? null;
}

/** Where a point sits relative to the straight start→end axis: cross-distance
 *  (km) off the line, and `t` in [0,1] for how far along it projects (0 = at
 *  start, 1 = at end). The geometry behind along-corridor scenic discovery. */
export function corridorOffset(
  via: LngLat,
  start: LngLat,
  end: LngLat,
): { offKm: number; t: number } {
  const axisLen = haversine(start, end);
  const np = nearestPointOnPolyline(via, [start, end]);
  return { offKm: np.distance / 1000, t: axisLen > 0 ? np.along / axisLen : 0 };
}

/**
 * Curated scenic roads found ALONG the route axis — not just near both
 * endpoints. This is what makes a long trip's "nicer way" discoverable: a
 * coastal road or river road that's a MID-trip detour (near neither the start
 * nor the destination) but clearly "on the way." Returns waypoints to inject so
 * the engine generates a genuinely-scenic, possibly much-longer candidate the
 * Scenic card can carry. Ranked by curated score, then closeness to the axis,
 * then least detour. Only for longer trips (short trips use the endpoint matcher
 * above). The caller decides whether the extra time is worth offering.
 */
export function scenicRoadsAlongCorridor(
  start: LngLat,
  end: LngLat,
  opts: { mode?: RoadMode; corridorKm?: number; maxDetourFactor?: number; maxRoads?: number } = {},
): ScenicRoadMatch[] {
  const mode = opts.mode ?? "driving";
  const directKm = distKm(start, end);
  if (directKm < 25) return []; // along-corridor discovery is for longer trips only
  // REACH SCALES WITH THE TRIP (Morgan, Aug 2: "if there is a great road close
  // enough relative to the distance of the trip it HAS to take that route").
  // A fixed 30km corridor made short trips and long trips reach equally far,
  // which is backwards — on a 280km drive a great road 60km off the line is a
  // reasonable ask, and the Lake Michigan shore sits ~50km off the
  // Holland→Traverse City line. Quarter of the direct distance, floor 30km.
  const corridorKm = opts.corridorKm ?? Math.max(30, directKm / 4);
  // 2× cap (Morgan's Aug 2 choice) — matches curatedScenicCap in scoring.ts,
  // replacing the old 1.8. Geometry-based approximation of the same bound.
  const maxDetourFactor = opts.maxDetourFactor ?? 2.0;
  const maxRoads = opts.maxRoads ?? 2;

  const cands: { match: ScenicRoadMatch; off: number; detour: number; score: number }[] = [];
  for (const road of SCENIC_ROADS) {
    if (!road.modes.includes(mode) || road.samples.length < 1) continue;
    const via = road.samples[Math.floor(road.samples.length / 2)];
    const { offKm, t } = corridorOffset(via, start, end);
    if (offKm > corridorKm || t < 0.1 || t > 0.9) continue; // off the axis, or hugging an endpoint
    const detourKm = distKm(start, via) + distKm(via, end) - directKm;
    if (detourKm > directKm * (maxDetourFactor - 1)) continue; // too far out of the way even for Scenic
    cands.push({ match: { road, waypoints: [via] }, off: offKm, detour: detourKm, score: road.score ?? 0 });
  }
  cands.sort((a, b) => b.score - a.score || a.off - b.off || a.detour - b.detour);
  return cands.slice(0, maxRoads).map((c) => c.match);
}

/**
 * MULTI-STOP scenic vias (Morgan: "Nice → Umbria → Rome shows only a Fast
 * route"). For a trip with intermediate stops, the single-corridor passes are
 * skipped (they only run start→dest with no waypoints), so a multi-stop trip
 * never gets a Pleasant alternative. This walks each consecutive LEG, finds the
 * best curated scenic via(s) for that leg, and returns the ordered list of
 * intermediate points to inject — leg-1 vias, then stop 1, then leg-2 vias, then
 * stop 2, … (start and final destination are supplied separately by the caller).
 * Threading ONE Mapbox route through that list yields a single pleasant route
 * that still honors every stop, in order — no geometry stitching required.
 *
 * `points` is the full ordered trip: [start, ...stops, dest].
 */
export function multiStopScenicVias(
  points: LngLat[],
  mode: RoadMode = "driving",
  opts: { perLegRoads?: number; maxWaypoints?: number } = {},
): LngLat[] {
  if (points.length < 3) return []; // need at least one intermediate stop
  const perLegRoads = opts.perLegRoads ?? 1;
  const out: LngLat[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    // Long legs (a road-trip stage like Nice→Umbria) use the ALONG-corridor
    // matcher — it finds a scenic road MID-leg, near neither endpoint. Short
    // legs (a multi-stop city walk) use the endpoint matcher, which wants the
    // curated road near both ends.
    const legKm = distKm(a, b);
    const matches =
      legKm >= 25
        ? scenicRoadsAlongCorridor(a, b, { mode, maxRoads: perLegRoads })
        : scenicRoadsForCorridor(a, b, {
            mode,
            maxRoads: perLegRoads,
            maxWaypoints: opts.maxWaypoints ?? 1,
          });
    for (const m of matches) out.push(...m.waypoints);
    // Re-insert the stop itself (but not the final destination — the caller
    // passes that as the route's endpoint).
    if (i < points.length - 2) out.push(b);
    // Mapbox caps total waypoints (~25); stop well short so the call never fails.
    if (out.length >= 22) break;
  }
  return out;
}

export interface GreatWalkChain {
  /** Ordered [lng,lat] waypoints to inject so the walk threads each segment. */
  waypoints: LngLat[];
  /** The curated segments threaded, in travel order (for the card blurb). */
  segments: { name: string; region: string }[];
}

/**
 * The "nice city streets" variety walk (Morgan). Distinct from the waterfront
 * walk (which FOLLOWS one linear feature), this stitches together a SEQUENCE of
 * curated pretty blocks — Jane St → Perry St → Bleecker St — that lie roughly
 * along the start→end corridor, so the walk wanders through charming streets
 * instead of the direct line or the river path.
 *
 * Method: take every curated walking segment within `corridorKm` of the direct
 * axis, bin them by how far along the trip they sit (`t`), and pick the
 * closest-to-the-line (tie: higher score) segment in each bin. That yields an
 * ordered, spread-out chain — forward-progressing, no backtrack — whose
 * midpoints become the injected waypoints. Returns null when fewer than
 * `minSegments` qualify (not enough curated coverage to be a real variety walk).
 */
export function greatWalkChainForCorridor(
  start: LngLat,
  end: LngLat,
  opts: { corridorKm?: number; maxSegments?: number; minSegments?: number } = {},
): GreatWalkChain | null {
  const corridorKm = opts.corridorKm ?? 0.7;
  const maxSegments = opts.maxSegments ?? 6;
  const minSegments = opts.minSegments ?? 2;
  const directKm = distKm(start, end);
  if (directKm < 0.4) return null; // too short to be worth a curated detour

  // Cheap spatial pre-filter. With ~40k curated walk segments loaded, scanning
  // every one with the corridorOffset trig is the walk's slow step. Almost all
  // segments are nowhere near this corridor, so reject them with a few float
  // compares (a bounding box around start↔end, expanded by the corridor width)
  // before doing any trig. The margin is generous enough that no true candidate
  // is ever dropped — corridorOffset still does the exact check.
  const minLng = Math.min(start[0], end[0]);
  const maxLng = Math.max(start[0], end[0]);
  const minLat = Math.min(start[1], end[1]);
  const maxLat = Math.max(start[1], end[1]);
  const latMargin = corridorKm / 85; // ~1.3× corridor width in degrees of lat
  const cosLat = Math.max(0.2, Math.cos(((minLat + maxLat) / 2) * Math.PI / 180));
  const lngMargin = corridorKm / (85 * cosLat);

  type Cand = { road: ScenicRoad; via: LngLat; offKm: number; t: number };
  const cands: Cand[] = [];
  for (const road of activeScenicRoads()) {
    if (!road.modes.includes("walking") || road.samples.length < 1) continue;
    const via = road.samples[Math.floor(road.samples.length / 2)];
    // Bounding-box reject before the expensive corridorOffset.
    if (
      via[0] < minLng - lngMargin || via[0] > maxLng + lngMargin ||
      via[1] < minLat - latMargin || via[1] > maxLat + latMargin
    ) continue;
    const { offKm, t } = corridorOffset(via, start, end);
    if (offKm > corridorKm || t <= 0.02 || t >= 0.98) continue; // off-axis or hugging an endpoint
    cands.push({ road, via, offKm, t });
  }
  if (cands.length < minSegments) return null;

  // Spread the picks along the corridor: one bin per ~0.4 km, capped. Closest to
  // the line wins each bin (then higher curated score), so the walk stays near
  // the direct path while still hopping the prettiest blocks.
  const bins = Math.min(maxSegments, Math.max(minSegments, Math.round(directKm / 0.4)));
  const chosen: Cand[] = [];
  for (let b = 0; b < bins; b++) {
    const lo = b / bins, hi = (b + 1) / bins;
    const inBin = cands.filter((c) => c.t >= lo && c.t < hi);
    if (inBin.length === 0) continue;
    inBin.sort((a, z) => a.offKm - z.offKm || (z.road.score ?? 0) - (a.road.score ?? 0));
    chosen.push(inBin[0]);
  }
  if (chosen.length < minSegments) return null;
  chosen.sort((a, z) => a.t - z.t);

  // Build waypoints that route THROUGH each chosen segment, not just near its
  // midpoint (Morgan: "pushed towards is not helpful — we need to go through
  // them"). A single midpoint via lets Mapbox snap to a parallel street; by
  // feeding several points ALONG the segment (start/mid/end of its samples)
  // the router is forced to thread the real block. Cap the total so we stay
  // within the routing engine's coordinate limit, and order every point
  // forward along the corridor so the path never doubles back.
  const MAX_WAYPOINTS = 16;
  const perSegMax = Math.max(1, Math.floor(MAX_WAYPOINTS / chosen.length));
  const collected: { p: LngLat; t: number }[] = [];
  for (const c of chosen) {
    const s = c.road.samples;
    let pts: LngLat[];
    if (s.length <= perSegMax) pts = s.slice();
    else if (perSegMax === 1) pts = [c.via];
    else if (perSegMax === 2) pts = [s[0], s[s.length - 1]];
    else pts = [s[0], s[Math.floor(s.length / 2)], s[s.length - 1]];
    for (const p of pts) collected.push({ p, t: corridorOffset(p, start, end).t });
  }
  // Global forward order so the threaded path never doubles back across segment
  // boundaries.
  collected.sort((a, b) => a.t - b.t);
  const waypoints: LngLat[] = collected.map((x) => x.p);

  return {
    waypoints,
    segments: chosen.map((c) => ({ name: c.road.name, region: c.road.region })),
  };
}
